import { type Attributes, SpanStatusCode, type Tracer } from '@opentelemetry/api';

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return typeof value === 'object' && value !== null && 'then' in value && typeof value.then === 'function';
}

/**
 * Runs `fn` inside an active span and returns its result. Async functions are awaited, so the span covers the whole
 * call; a thrown or rejected error is recorded on the span and re-thrown unchanged.
 */
export function runInSpan<T>(tracer: Tracer, name: string, fn: () => T, attributes?: Attributes): T {
  return tracer.startActiveSpan(name, { attributes }, (span) => {
    const fail = (error: unknown) => {
      const exception = error instanceof Error ? error : new Error(String(error));
      span.recordException(exception);
      span.setStatus({ code: SpanStatusCode.ERROR, message: exception.message });
      span.end();
    };

    try {
      const result = fn();
      if (isThenable(result)) {
        return result.then(
          (value) => {
            span.end();
            return value;
          },
          (error: unknown) => {
            fail(error);
            throw error;
          }
        ) as typeof result;
      }
      span.end();
      return result;
    } catch (error) {
      fail(error);
      throw error;
    }
  });
}
